/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import axios from 'axios'
axios.defaults.headers.common['Accept'] = 'application/json'

/**
 * Forms don't need pagination, they should always be less than 10
 * @param context
 * @returns {Promise<unknown>}
 */

const formTypes = []
function prepareParams(config) {
    return new Promise((resolve, reject) => {
        var field = config.sort

        if (config.sortDirection === 'desc') {
            field = '-' + field
        }

        var params = {
            page: config.page,
            include: config.include,
            sort: field,
            per_page: config.per_page
        }

        if (config.filters) {
            for (const [key, value] of Object.entries(config.filters)) {
                params[`filter[${key}]`] = value
            }
        }

        resolve(params)
    })
}

export default {
    getUsers({ state, rootState, commit }) {
        return new Promise((resolve, reject) => {
            try {
                prepareParams(state.usersConfig).then((params) => {
                    axios
                        .get(`${rootState.baseURL}/users/find`, { params })
                        .then((response) => {
                            const users = response.data.users
                            commit('setUsers', users)
                            resolve(users)
                        })
                        .catch((error) => {
                            reject(error)
                        })
                })
            } catch (error) {
                reject(error)
            }
        })
    },
    /**
     * Citizens known by the company, with their local role.
     * The endpoint takes plain `search` / `role` query params (no filter[] wrapper).
     */
    getCitizens({ state, rootState, commit }) {
        return new Promise((resolve, reject) => {
            const config = state.citizensConfig
            const params = {
                page: config.page,
                per_page: config.per_page
            }

            if (config.search) params.search = config.search
            if (config.role) params.role = config.role

            axios
                .get(`${rootState.baseURL}/citizens`, { params })
                .then((response) => {
                    const citizens = response.data.citizens || response.data
                    commit('setCitizens', citizens)
                    resolve(citizens)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    /**
     * Creates a citizen account already holding a local role.
     * NOTE: the POST endpoint has not been specified yet, the payload below is
     * the assumed counterpart of GET /citizens — adjust once it is confirmed.
     */
    createCitizen({ rootState, commit }, citizen) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/citizens`, { ...citizen })
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((e) => {
                    reject(e.response && e.response.data ? e.response.data.errors || e.response.data : e)
                })
        })
    },
    getUsersFilters({ state, rootState, commit }) {
        return new Promise((resolve, reject) => {
            commit('setUsersFilters', { formTypes })
            resolve()
        })
    },
    createUser({ state, rootState, commit }, user) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/users`, { ...user })
                .then(({ data }) => {
                    commit('setNewUser', user)
                    resolve(data)
                })
                .catch((e) => {
                    const error = e.response.data.errors
                    reject(error)
                })
        })
    },
    updateUser({ state, rootState, commit }, user) {
        return new Promise((resolve, reject) => {
            axios
                .put(`${rootState.baseURL}/users/${user.id}`, { ...user })
                .then(({ data }) => {
                    resolve(user)
                })
                .catch((e) => {
                    const error = e.response.data.errors
                    reject(error)
                })
        })
    },
    editUser({ rootState, state, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/users/${id}`)
                .then((response) => {
                    const user = response.data.user || response.data
                    if (user.roles && user.roles.length) {
                        user.role = user.roles[0].name
                    }
                    commit('setNewUser', user)
                    resolve(user)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getMyCompany({ rootState, commit }) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/mycompany`)
                .then(({ data }) => {
                    commit('setCompany', data)
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getCompanySettings({ rootState, commit }, axiosConfig = {}) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/company/settings`, axiosConfig)
                .then(({ data }) => {
                    commit('setCompanySettings', data.settings)
                    commit('SetCompanyHeaderMail', data.header_mail)
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    sendCompanyAdminEmailAndLogo({ state, rootState }) {
        return new Promise((resolve, reject) => {
            let body = {}
            body.mail_admin = state.companySettings.mail_admin
            body.settings_logo = state.companySettings.settings_logo
            body.logo_settings = state.companySettings.logo_settings
            axios
                .post(`${rootState.baseURL}/company/settings`, body)
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    sendCompanySettings({ state, rootState }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/company/settings`,
                    state.companySettings
                )
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    sendCompanyHeaderMail({ state, rootState }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/company/settings/header`,
                    state.companyHeaderMail
                )
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    editWebhook({ rootState, state, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/webhooks/${id}`)
                .then(({ data }) => {
                    commit('setNewWebhook', data.webhook)
                    resolve(data.webhook)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    createWebhook({ state, rootState, commit }, data) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/webhooks`, state.newWebhook)
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    updateWebhook({ state, rootState, commit }, data) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/webhooks/${state.newWebhook.id}`,
                    state.newWebhook
                )
                .then(({ data }) => {
                    resolve(data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getUserProfile({ rootState }, userId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/users/${userId}/profile`)
                .then((response) => {
                    resolve(response.data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getCitizen({ rootState }, id) {
        return new Promise((resolve, reject) => {
            try {
                axios
                    .get(`${rootState.baseURL}/citizen/${id}`)
                    .then((response) => {
                        resolve(response.data.citizen)
                    })
                    .catch((error) => {
                        reject(error)
                    })
            } catch (error) {
                reject(error)
            }
        })
    }
}
